import React, {useEffect, useMemo, useState} from 'react';
import PageShell from './PageShell';
import DialogFooter from './components/DialogFooter';
import Pagination from './components/Pagination';
import type {MetrcFacilityDocument} from './types/index';
import LinkProductSelector from "./components/LinkProductSelector";
import { useMetrcProductOptions } from './hooks/useMetrcImportOptions';
import { useClientPagination } from './hooks/useClientPagination';

type MetrcProductsDialogProps = {
  organizationId?: number;
  marketId: number;
  market: string;
  username?: string;
  facilities?: MetrcFacilityDocument[];
  existingProducts?: any[];
  onClose: () => void;
  onBack: () => void;
  onSave: (payload: any) => void;
};

export default function MetrcProductsDialog({
                                              organizationId,
                                              marketId,
                                              market,
                                              username,
                                              facilities = [],
                                              onClose,
                                              onBack,
                                              onSave
                                            }: MetrcProductsDialogProps) {

  const { availableProducts, setAvailableProducts, loading, loadError } =
    useMetrcProductOptions(organizationId, marketId, facilities);
  const [search, setSearch] = useState('');

  const filteredProducts = useMemo(() => {
    if (!search.trim()) return availableProducts;
    const term = search.trim().toLowerCase();
    return availableProducts.filter(p => {
      const facility = facilities.find(f => f.id === p.facilityId);
      return (
        (p.name || '').toLowerCase().includes(term) ||
        (facility?.license || '').toLowerCase().includes(term) ||
        (facility?.name || '').toLowerCase().includes(term)
      );
    });
  }, [availableProducts, search, facilities]);

  const {
    page,
    totalPages,
    pagedItems: paged,
    resetPage,
    previousPage,
    nextPage,
  } = useClientPagination(filteredProducts, 12);

  useEffect(() => {
    resetPage();
  }, [organizationId, marketId, facilities]);

  const selected = useMemo(() => {
    return availableProducts.filter(p => p.selected);
  }, [availableProducts]);

  const toggle = (id) => {
    setAvailableProducts(prev =>
      prev.map(p =>
        p.id === id
          ? {...p, selected: !p.selected}
          : p
      )
    );
  };

  const setAll = () => {
    const filteredIds = new Set(filteredProducts.map(p => p.id));
    const anyUnselected = filteredProducts.some(p => !p.selected);
    setAvailableProducts(prev =>
      prev.map(p =>
        filteredIds.has(p.id) ? {...p, selected: anyUnselected} : p
      )
    );
  };

    const assignProduct = (product, item) => {
        product['dbProduct'] = item;
    };

  const footerButtons = (
    <DialogFooter withTopDivider dividerStyle={{marginTop: 12, marginBottom: 10}}>
      <button className="button secondary" onClick={onBack} data-testid="metrc-products-back">Back</button>
      <button
        className="button"
        onClick={() => onSave({
          selectedProducts: selected
        })
        }
        disabled={selected.length === 0}
        data-testid="metrc-products-import"
      >
        Import Products
      </button>
    </DialogFooter>
  );

  return (
    <PageShell
      contextLabel="Integrations"
      title="Import Products"
      onClose={onClose}
      framed={false}
      testId="metrc-products"
      className="metrc-import-shell"
      preview={(
        <div className="card" style={{padding: 16}}>
          <div className="muted" style={{fontSize: 12, fontWeight: 800, marginBottom: 8}}>Preview</div>
          <div style={{fontWeight: 800, fontSize: 16, marginBottom: 8}}>Product Import</div>
          <div className="muted" style={{fontSize: 13, display: 'grid', gap: 6, marginBottom: 10}}>
            <div><span style={{fontWeight: 700, color: 'var(--primary)'}}>Market:</span> {market || '—'}</div>
            <div><span style={{fontWeight: 700, color: 'var(--primary)'}}>Username:</span> {username || '—'}</div>
            <div><span style={{
              fontWeight: 700,
              color: 'var(--primary)'
            }}>Available Products:</span> {availableProducts.length}</div>
            <div><span style={{fontWeight: 700, color: 'var(--primary)'}}>Selected:</span> {selected.length}</div>
          </div>
          <div className="divider" style={{margin: '12px 0'}}/>
          <div className="muted" style={{fontSize: 12, fontWeight: 800, marginBottom: 8}}>Selected Products</div>
          <div style={{display: 'grid', gap: 8}}>
            {selected.length === 0 ? (
              <div className="muted" style={{fontSize: 12}}>None selected yet.</div>
            ) : (
              selected
                .slice(0, 6)
                .map((p) => {
                  const facility = facilities.find(f => f.id === p.facilityId)
                  return (
                    <div key={p.id} className="card" style={{padding: 10}}>
                      <div style={{fontWeight: 800, fontSize: 13}}>{p.name}</div>
                      <div className="muted"
                           style={{fontSize: 12}}>{facility?.license} - {facility?.name}</div>
                      <div className="muted" style={{fontSize: 12, marginTop: 4}}>
                        <span style={{fontWeight: 700, color: 'var(--primary)'}}>Maps to:</span>{' '}
                        {/*{(() => {
                          const mappedId = productMappings[String(p.id)] || '';
                          if (!mappedId) return '—';
                          const opts = productMapOptions[String(p.id)] || [];
                          return opts.find((x) => x.id === mappedId)?.label || existingProductLabelById[mappedId] || '—';
                        })()}*/}
                      </div>
                    </div>
                  )
                })
            )}
            {selected.length > 6 && (
              <div className="muted" style={{fontSize: 12}}>+{selected.length - 6} more</div>
            )}
          </div>
        </div>
      )}
      footer={footerButtons}
    >
      <div className="form-grid">
        <div className="field full-span">
          <label className="label">Market</label>
          <input className="input" value={market || '—'} readOnly disabled data-testid="metrc-products-market"/>
        </div>
        <div className="field full-span" style={{marginTop: 8}}>
          <div style={{marginBottom: 8}}>
            <div>
              <label className="label" style={{marginBottom: 0}}>Products to import</label>
              <div className="muted" style={{fontSize: 12}}>Showing products from selected facilities that are not yet
                in the system
              </div>
            </div>
          </div>
          <div style={{
            border: '1px solid var(--border)',
            borderRadius: 12,
            padding: 8,
            minHeight: 500,
            overflowY: 'auto'
          }}>
            <div style={{ padding: '4px 4px 8px' }}>
              <input
                className="input"
                type="text"
                placeholder="Search by product name, facility name, or license..."
                value={search}
                onChange={(e) => { setSearch(e.target.value); resetPage(); }}
                data-testid="metrc-products-search"
                style={{ width: '100%' }}
              />
            </div>
            {loading && (
              <div className="muted" style={{padding: 8}}>Loading products...</div>
            )}
            {!loading && loadError && (
              <div className="error-text" style={{padding: 8}}>{loadError}</div>
            )}
            {!loading && filteredProducts.length > 0 && (
              <div style={{
                display: 'grid',
                gridTemplateColumns: 'auto 1fr 340px',
                alignItems: 'center',
                gap: 10,
                padding: '6px 4px',
                borderBottom: '1px solid var(--border)'
              }}>
                <input
                  id={'pg-select-all'}
                  type="checkbox"
                  checked={filteredProducts.length > 0 && filteredProducts.every(p => p.selected)}
                  onChange={setAll}
                  aria-label="Select all products"
                  data-testid="metrc-products-select-all"
                />
                <label htmlFor={'pg-select-all'} className="muted" style={{fontSize: 12, fontWeight: 700}}>Select
                  All</label>
                <div className="muted" style={{textAlign: 'right', fontSize: 12, fontWeight: 700}}>Map to existing
                  (optional)
                </div>
              </div>
            )}
            {!loading &&
              !loadError &&
              availableProducts.length === 0 && (
              <div className="muted" style={{padding: 8}}>No new products to import.</div>
            )}
            {!loading &&
              !loadError &&
              availableProducts.length > 0 &&
              filteredProducts.length === 0 && (
              <div className="muted" style={{padding: 8}}>No products match your search.</div>
            )}
            {paged.map((p, index) => {

              const facility = facilities
                .find(f => f.id === p.facilityId)

              return (
                <div key={index} style={{
                  display: 'grid',
                  gridTemplateColumns: 'auto minmax(0, 1fr) minmax(0, 340px)',
                  alignItems: 'center',
                  gap: 10,
                  padding: '6px 4px'
                }}>
                  <input
                    id={`prod-${index}`}
                    type="checkbox"
                    checked={selected.some(s => s.id == p.id) ?? false}
                    onChange={() => toggle(p.id)}
                    data-testid={`metrc-products-check-${String(p.id || '').toLowerCase()}`}
                  />
                  <label htmlFor={`prod-${index}`} style={{display: 'block', cursor: 'pointer', minWidth: 0}}>
                    <div style={{fontWeight: 700}}>{p.name}</div>
                    <div className="muted"
                         style={{fontSize: 12}}>{facility?.license||''} - {facility?.name||''}</div>
                  </label>
                      {
                          p.selected && <LinkProductSelector mappedValue={undefined} organizationId={organizationId} productName={p.name} onSelect={(item) => assignProduct(p, item)} />
                  }
                </div>
              );
            })}
          </div>
          <Pagination
            page={page}
            totalPages={totalPages}
            onPrev={previousPage}
            onNext={nextPage}
            prevLabel="◀"
            nextLabel="▶"
            format="of"
          />
        </div>
      </div>
    </PageShell>
  );
}
